import {
  getCssPropertyValue,
  setCSSRootValue,
  setCssPropertyValue
} from '../../../ts/helper';
import { FvCustomEventNames } from '../../../ts/events';
import { throttle } from 'throttle-debounce';

/**
 * Callback data for when the visibility of the header changes
 */
interface VisibilityCallbackData {
  /** Current scroll direction */
  direction: 'visible' | 'hidden';
}

export const BasisHeaderLock = () => {
  document
    .querySelector('[is="basis-header"]')
    ?.setAttribute('data-locked', 'true');
};

export const BasisHeaderUnLock = () => {
  document.querySelector('[is="basis-header"]')?.removeAttribute('data-locked');
};

/**
 * Friendation component
 * @param component Instance of this _Friendation_ component
 */
const BasisHeader = (component: HTMLElement) => {
  /** Current state of header */
  let currentState: VisibilityCallbackData['direction'] = 'visible';

  /** Last state of header */
  let lastState: VisibilityCallbackData['direction'] = 'visible';

  /**
   * Update the header, based on the {@link currentState}
   */
  const update = () => {
    /** Make changes based on the {@link currentState} */
    component.dataset.currentState = currentState;

    setCSSRootValue(
      '--sticky-top',
      currentState === 'hidden'
        ? '0px'
        : 'calc(0px + var(--header-height) + var(--preheader-height))'
    );

    /**
     * Dispatch custom `Event`s that signal the headers visibility has been changed. Check the documentation of {@link FvCustomEventNames}
     */
    document.dispatchEvent(
      currentState === 'hidden'
        ? new Event(FvCustomEventNames.BasisHeaderOut)
        : new Event(FvCustomEventNames.BasisHeaderIn)
    );
  };

  /**
   * Show the header
   */
  const show = () => {
    currentState = 'visible';
    update();
  };

  /**
   * Hide the header
   */
  const hide = () => {
    currentState = 'hidden';
    update();
  };

  /**
   * Behaviour of the component when the user scrolls up or down ({@link currentState})
   * @param minDistance From the top of the page, you have to scroll down a minimum distance before the visibility of {@link component} can change at all.
   * @param upThreshold Scroll up more than this distance in one go: show {@link component}.
   * @param downThreshold Scroll down more than this distance in one go: hide {@link component}.
   * @param callback Callback function is triggered when {@link currentState} changes
   * @requires {@link component}
   * @example
   *   handleVisibility(200, 300, 130, (data) => {
   *     console.log(data.direction) // 'visible' | 'hidden'
   *   })
   */
  const scrollBehaviour = (
    minDistance: number,
    upThreshold: number,
    downThreshold: number
  ) => {
    /** Represents the window.scrollY position, when {@link currentState} was changed last */
    let lastPos = 0;

    /** Timeout for {@link lastPos} */
    let timeout: ReturnType<typeof setTimeout>;

    /** Time in milliseconds until {@link onScroll} is called again */
    const tick = 100;

    /**
     * The header will hide/show as soon as the user has scrolled a minimum distance of pixels ({@link downThreshold})
     */
    const onScroll = () => {
      requestAnimationFrame(() => {
        clearTimeout(timeout);

        /** Set admin-bar-height */
        const adminBarEl: HTMLElement = document.querySelector('admin-bar');
        if (adminBarEl)
          setCssPropertyValue(
            '--admin-bar-height',
            getCssPropertyValue('--admin-bar-height', adminBarEl)
          );

        const preHeaderHeight = document
          .querySelector('[is="basis-pre-header"]')
          ?.scrollHeight.toString();

        setCSSRootValue('--preheader-height', `${preHeaderHeight}px`);

        /** The scroll position */
        const y = window.pageYOffset || document.documentElement.scrollTop;

        /** Do not detect changes while locked */
        if (component.hasAttribute('data-locked')) {
          currentState =
            component.dataset.currentState === 'visible' ? 'visible' : 'hidden';
        } else {
          /** The header is always visible, until the user scrolled a {@link minDistance} */
          if (y < minDistance) {
            currentState = 'visible';
            component.dataset.scrollTop = 'true';
          } else {
            component.dataset.scrollTop = 'false';

            /** Scrolling down: You have to scroll down a {@link downThreshold} to change the {@link currentState} to `'hidden'` */
            if (y > lastPos + downThreshold) {
              lastPos = y;
              currentState = 'hidden';
            }

            /** Scrolling up: You have to scroll up a {@link upThreshold} to change the {@link currentState} to `'visible'` */
            if (y < lastPos - upThreshold) {
              lastPos = y;
              currentState = 'visible';
            }
          }
        }

        if (currentState !== lastState) {
          if (currentState === 'visible') {
            show();
          } else {
            hide();
          }
        }

        lastState = currentState;

        /** Timeout value for {@link timeout}. Time in milliseconds. Keep this above the value of {@link tick} so we don't run into trouble */
        const resetTimeoutAfter: number = tick * 2;

        /** Save the last known position in {@link lastPos} */
        timeout = setTimeout(() => {
          lastPos = y;
        }, resetTimeoutAfter);
      });
    };

    const throttleTick = throttle(100, () => {
      onScroll();
    });

    window.addEventListener('scroll', throttleTick);
    window.addEventListener('resize', throttleTick);
    onScroll();
  };

  /** When any element in the {@link component} is focussed, show the header */
  const handleFocus = () => {
    /** Focussable elements */
    const items: (HTMLButtonElement | HTMLAnchorElement)[] = Array.from(
      component.querySelectorAll('a, button')
    );
    items.forEach((item) => {
      item.addEventListener('focus', () => {
        // Create Event
        const evt = new Event(FvCustomEventNames.BasisHeaderOpen);

        // Fire Event on `document`
        document.dispatchEvent(evt);
      });
    });
  };

  /** Listen for scroll events and adjust the visibility of the header */
  scrollBehaviour(200, 300, 130);

  /** Listen for custom event to open the header */
  document.addEventListener(FvCustomEventNames.BasisHeaderOpen, () => {
    show();
  });

  /** Listen for custom event to close the header */
  document.addEventListener(FvCustomEventNames.BasisHeaderClose, () => {
    hide();
  });

  /** Listen for focus events on children to open the header */
  handleFocus();
};

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="basis-header"]';

/** Function that handles a single instance of this _Friendation_ component */
const classFunction: (component: HTMLElement) => void = BasisHeader;

/**
 * Friendation component
 */
export const aHeader = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((x) => {
    classFunction.call(this, x);
  });
};
